import type { ReactNode } from "react";
import type {
  ArticleBlock,
  ArticleDocument,
  ArticleInlineNode,
  ArticleMediaAsset,
  ArticleTextNode,
} from "./models";

export type ArticleImageRenderer = (asset: ArticleMediaAsset, alt: string) => ReactNode;
export type ArticleVideoRenderer = (asset: ArticleMediaAsset, options: { poster?: ArticleMediaAsset; autoplay?: boolean; loop?: boolean; muted?: boolean; controls?: boolean }) => ReactNode;

type ArticleRendererCoreProps = {
  document: ArticleDocument;
  media: ArticleMediaAsset[];
  renderImage?: ArticleImageRenderer;
  renderVideo?: ArticleVideoRenderer;
};

function renderTextNode(node: ArticleTextNode, key: string): ReactNode {
  let rendered: ReactNode = node.text;
  if (node.marks?.includes("bold")) rendered = <strong>{rendered}</strong>;
  if (node.marks?.includes("italic")) rendered = <em>{rendered}</em>;
  if (node.marks?.includes("underline")) rendered = <u>{rendered}</u>;
  return <span key={key}>{rendered}</span>;
}

function renderInline(content: ArticleInlineNode[], keyPrefix: string): ReactNode[] {
  return content.map((node, index) => {
    const key = `${keyPrefix}-${index}`;
    if (node.type === "text") return renderTextNode(node, key);
    const external = /^https?:\/\//i.test(node.href);
    return (
      <a href={node.href} key={key} rel={external ? "noopener noreferrer" : undefined}>
        {node.content.map((child, childIndex) => renderTextNode(child, `${key}-${childIndex}`))}
      </a>
    );
  });
}

function renderBlock(
  block: ArticleBlock,
  index: number,
  mediaById: Map<string, ArticleMediaAsset>,
  renderImage?: ArticleImageRenderer,
  renderVideo?: ArticleVideoRenderer,
) {
  const key = `article-block-${index}`;
  if (block.type === "paragraph") return <p>{renderInline(block.content, key)}</p>;
  if (block.type === "heading") {
    return block.level === 3
      ? <h3>{renderInline(block.content, key)}</h3>
      : <h2>{renderInline(block.content, key)}</h2>;
  }
  if (block.type === "list") {
    const items = block.items.map((item, itemIndex) => <li key={`${key}-${itemIndex}`}>{renderInline(item, `${key}-${itemIndex}`)}</li>);
    return block.style === "unordered" ? <ul>{items}</ul> : <ol>{items}</ol>;
  }
  if (block.type === "blockquote") {
    return (
      <blockquote>
        <p>{renderInline(block.content, key)}</p>
        {block.cite ? <cite>{block.cite}</cite> : null}
      </blockquote>
    );
  }
  if (block.type === "note") {
    return (
      <aside className="article-note">
        {block.title ? <strong>{block.title}</strong> : null}
        <p>{renderInline(block.content, key)}</p>
      </aside>
    );
  }

  if (block.type === "video") {
    const asset = mediaById.get(block.mediaId);
    if (!asset || asset.kind !== "video") return null;
    const poster = block.posterMediaId ? mediaById.get(block.posterMediaId) : undefined;
    return <figure className="article-inline-figure article-inline-video">{renderVideo ? renderVideo(asset, { poster, autoplay: block.autoplay, loop: block.loop, muted: block.muted, controls: block.controls }) : <video src={asset.src} width={asset.width} height={asset.height} controls={block.controls ?? true} loop={block.loop} muted={block.muted ?? true} playsInline />}{block.caption ? <figcaption>{block.caption}</figcaption> : null}</figure>;
  }
  if (block.type !== "image") return null;
  const asset = mediaById.get(block.mediaId);
  if (!asset) return null;
  return (
    <figure className="article-inline-figure">
      {renderImage
        ? renderImage(asset, block.alt)
        : <img src={asset.src} alt={block.alt} width={asset.width} height={asset.height} />}
      {block.caption ? <figcaption>{block.caption}</figcaption> : null}
    </figure>
  );
}

export function ArticleRendererCore({ document, media, renderImage, renderVideo }: ArticleRendererCoreProps) {
  const mediaById = new Map(media.map((asset) => [asset.id, asset]));
  return document.blocks.map((block, index) => (
    <div className={`article-block article-block-${block.type}`} key={`article-block-${index}`}>
      {renderBlock(block, index, mediaById, renderImage, renderVideo)}
    </div>
  ));
}
